import { create } from 'zustand';
import {
  uploadBook,
  uploadBookFromDrive,
  DriveReauthRequiredError,
  type UploadProgress,
} from '../api/books';
import { queryClient } from '../lib/queryClient';
import { bookKeys } from '../hooks/useBooks';

// How many uploads run at once. Unlike video (uploadStore.ts, CONCURRENCY=1 —
// server buffers the whole file + spawns ffmpeg), the book upload handler is
// cheap per-file (hash, R2 put, DB row, enqueue) and processing happens async
// on the backend queue, not inline in the request. A modest cap avoids piling
// up too many concurrent in-memory ReadAll buffers server-side (books top out
// at 500MB vs video's 5GB) while still uploading a large batch far faster than
// serial.
const CONCURRENCY = 4;

export type BookUploadStatus =
  | 'queued'
  | 'uploading'
  | 'done'
  | 'duplicate'
  | 'failed'
  | 'needs_reauth';

interface LocalUploadSource {
  source: 'local';
  file: File;
}

interface DriveUploadSource {
  source: 'drive';
  fileId: string;
  name: string;
}

export type BookUploadSource = LocalUploadSource | DriveUploadSource;

export type BookUploadItem = BookUploadSource & {
  localId: string;
  status: BookUploadStatus;
  progress: UploadProgress | null;
  bookId?: number;
  error?: string;
};

export function uploadItemName(item: BookUploadItem): string {
  return item.source === 'local' ? item.file.name : item.name;
}

function isTerminal(item: BookUploadItem): boolean {
  return item.status === 'done' || item.status === 'duplicate' || item.status === 'failed';
}

interface BookUploadStore {
  items: BookUploadItem[];
  enqueue: (files: File[]) => void;
  enqueueFromDrive: (files: { fileId: string; name: string }[]) => void;
  retry: (localId: string) => void;
  clearSettled: () => void;
}

let idCounter = 0;

// Only the fields common to every source variant are ever patched in place —
// source-discriminant fields (source/file/fileId/name) are fixed at
// enqueue time and never change, so a full Partial<BookUploadItem> merge
// isn't type-safe against the discriminated union (TS can't prove the
// spread result still matches one specific arm).
type BookUploadPatch = Partial<Pick<BookUploadItem, 'status' | 'progress' | 'bookId' | 'error'>>;

export const useBookUploadStore = create<BookUploadStore>((set, get) => {
  function patch(localId: string, next: BookUploadPatch): void {
    set((state) => ({
      items: state.items.map((i) => (i.localId === localId ? { ...i, ...next } : i)),
    }));
  }

  async function runOne(localId: string): Promise<void> {
    const item = get().items.find((i) => i.localId === localId);
    if (!item) return;

    patch(localId, {
      status: 'uploading',
      progress:
        item.source === 'local'
          ? { loaded: 0, total: item.file.size, percent: 0 }
          : null,
      error: undefined,
    });

    try {
      const result =
        item.source === 'local'
          ? await uploadBook({
              file: item.file,
              onProgress: (p) => patch(localId, { progress: p }),
            })
          : await uploadBookFromDrive(item.fileId);

      const finalProgress =
        item.source === 'local'
          ? { loaded: item.file.size, total: item.file.size, percent: 100 }
          : null;

      if (result.duplicate) {
        patch(localId, { status: 'duplicate', bookId: result.book.id, progress: finalProgress });
      } else {
        patch(localId, { status: 'done', bookId: result.id, progress: finalProgress });
      }

      queryClient.invalidateQueries({ queryKey: bookKeys.all });
    } catch (err) {
      if (err instanceof DriveReauthRequiredError) {
        patch(localId, { status: 'needs_reauth', error: err.message });
      } else {
        // Per-item isolation: mark failed, leave the rest of the batch running.
        // Retry re-enqueues this same item.
        patch(localId, { status: 'failed', error: (err as Error).message });
      }
    }

    pump();
  }

  // Fill open slots up to the concurrency cap with queued items, oldest first.
  function pump(): void {
    const items = get().items;
    const inFlight = items.filter((i) => i.status === 'uploading').length;
    let free = CONCURRENCY - inFlight;
    if (free <= 0) return;

    for (const item of items) {
      if (free <= 0) break;
      if (item.status === 'queued') {
        free--;
        void runOne(item.localId);
      }
    }
  }

  return {
    items: [],

    enqueue: (files) => {
      if (files.length === 0) return;

      const newItems: BookUploadItem[] = files.map((file) => ({
        localId: `bup-${++idCounter}`,
        source: 'local',
        file,
        status: 'queued',
        progress: null,
      }));

      set((state) => ({ items: [...state.items, ...newItems] }));
      pump();
    },

    enqueueFromDrive: (files) => {
      if (files.length === 0) return;

      const newItems: BookUploadItem[] = files.map(({ fileId, name }) => ({
        localId: `bup-${++idCounter}`,
        source: 'drive',
        fileId,
        name,
        status: 'queued',
        progress: null,
      }));

      set((state) => ({ items: [...state.items, ...newItems] }));
      pump();
    },

    retry: (localId) => {
      const item = get().items.find((i) => i.localId === localId);
      if (!item || (item.status !== 'failed' && item.status !== 'needs_reauth')) return;
      set((state) => ({
        items: state.items.map((i) =>
          i.localId === localId ? { ...i, status: 'queued', error: undefined, progress: null } : i
        ),
      }));
      pump();
    },

    clearSettled: () =>
      set((state) => ({
        items: state.items.filter((i) => !isTerminal(i)),
      })),
  };
});
